// `buscar_en_el_chat`: buscar en los mensajes viejos de la conversación —
// ADR-2026-09-13-el-chat-se-compacta-y-se-puede-buscar.
//
// Cuando un chat se pasa de largo, la cabeza del historial deja de viajar textual y se
// reemplaza por un resumen (`compactacionDeContexto.ts`). El resumen conserva el hilo y
// los ids, pero no las palabras: "¿te acordás de lo que te dije al principio sobre la
// campaña de invierno?" no se puede contestar leyéndolo.
//
// Esta herramienta cierra ese agujero. El chat COMPLETO sigue vivo en `messages` (y en
// `agent_chats`), así que la búsqueda es local: no hay red, no hay costo y no hay
// embeddings. Es un scoring por palabra sobre el texto que el modelo YA vio en su
// momento —incluidos los `[registro: …]`, que son donde están los ids—, y devuelve
// recortes con su posición para que el agente pueda ubicarse en la conversación.
//
// Puro, para poder probarlo.

import { marcadorDeAdjuntos, type AdjuntoDeMensaje } from './adjuntosDelMensaje';

export interface MensajeBuscable {
  role: 'user' | 'assistant';
  text: string;
  adjuntos?: AdjuntoDeMensaje[];
  registros?: string[];
  graficos?: Array<{ titulo: string }>;
  toolCards?: Array<{ kind: string; input?: unknown }>;
}

export const MAX_RESULTADOS = 10;
const RESULTADOS_POR_DEFECTO = 5;
/** Caracteres de contexto a cada lado del mejor acierto. */
const RADIO_RECORTE = 260;

/** Palabras que aparecen en cualquier mensaje y sólo agregan ruido al puntaje. */
const VACIAS = new Set([
  'a','al','ante','como','con','cual','cuando','de','del','desde','donde','el','ella','ellos','en','entre','era','es','esa','ese','eso','esta','este','esto','fue','ha','hay','la','las','le','les','lo','los','mas','me','mi','muy','ni','no','nos','o','para','pero','por','que','se','ser','si','sin','sobre','son','su','sus','te','tu','un','una','uno','y','ya','yo',
]);

const sinAcentos = (s: string): string =>
  s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

export function terminosDeLaConsulta(consulta: string): string[] {
  const brutos = sinAcentos(consulta).split(/[^a-z0-9_-]+/).filter(Boolean);
  const utiles = brutos.filter((t) => t.length >= 3 && !VACIAS.has(t));
  // Si la consulta era toda palabras vacías o muy cortas ("qué le dije de X"), se usa lo
  // que haya antes que devolver cero resultados por una regla de higiene.
  return (utiles.length > 0 ? utiles : brutos).slice(0, 12);
}

const clip = (s: string, n: number): string => (s.length > n ? `${s.slice(0, n)}…` : s);

/** Todo lo indexable de un mensaje: lo que el usuario escribió, lo que el agente contestó,
 *  con qué vino adjunto y los registros de lo que usó (donde viven los ids). */
export function textoBuscableDe(m: MensajeBuscable): string {
  const partes: string[] = [m.text || ''];
  if (m.role === 'user' && m.adjuntos?.length) partes.push(marcadorDeAdjuntos(m.adjuntos));
  for (const g of m.graficos ?? []) partes.push(`[gráfico: ${g.titulo}]`);
  for (const c of m.toolCards ?? []) {
    partes.push(`[${c.kind}${c.input ? ` ${clip(JSON.stringify(c.input), 600)}` : ''}]`);
  }
  for (const r of m.registros ?? []) partes.push(r);
  return partes.filter(Boolean).join('\n');
}

interface Acierto {
  indice: number;
  puntaje: number;
  posicion: number;
}

function puntuar(texto: string, terminos: string[]): { puntaje: number; posicion: number } {
  const plano = sinAcentos(texto);
  let puntaje = 0;
  let distintos = 0;
  let posicion = -1;
  for (const t of terminos) {
    let desde = 0;
    let veces = 0;
    for (;;) {
      const i = plano.indexOf(t, desde);
      if (i === -1) break;
      veces++;
      if (posicion === -1 || i < posicion) posicion = i;
      desde = i + t.length;
      if (veces >= 8) break;
    }
    if (veces > 0) {
      distintos++;
      puntaje += veces;
    }
  }
  // Un mensaje que toca TODOS los términos vale mucho más que uno que repite uno solo.
  if (distintos > 0) puntaje += distintos * distintos * 3;
  return { puntaje, posicion: posicion === -1 ? 0 : posicion };
}

function recorte(texto: string, posicion: number): string {
  const inicio = Math.max(0, posicion - RADIO_RECORTE);
  const fin = Math.min(texto.length, posicion + RADIO_RECORTE);
  const cuerpo = texto.slice(inicio, fin).replace(/\s+/g, ' ').trim();
  return `${inicio > 0 ? '…' : ''}${cuerpo}${fin < texto.length ? '…' : ''}`;
}

const QUIEN = { user: 'El usuario', assistant: 'Vos' } as const;

/**
 * Devuelve el texto del `tool_result`. Siempre en el mismo formato: una línea por
 * resultado, con la POSICIÓN del mensaje (para que el agente sepa si es del principio o
 * de recién) y el recorte alrededor del acierto.
 */
export function buscarEnElChat(
  mensajes: MensajeBuscable[],
  consulta: string,
  limite: number = RESULTADOS_POR_DEFECTO,
): string {
  const terminos = terminosDeLaConsulta(consulta || '');
  const total = mensajes.length;
  if (terminos.length === 0) {
    return 'No entendí qué buscar. Pasá en `consulta` las palabras clave (un producto, una campaña, un pedido concreto).';
  }

  const textos = mensajes.map(textoBuscableDe);
  const aciertos: Acierto[] = [];
  for (let i = 0; i < total; i++) {
    const { puntaje, posicion } = puntuar(textos[i], terminos);
    if (puntaje > 0) aciertos.push({ indice: i, puntaje, posicion });
  }
  if (aciertos.length === 0) {
    return `Busqué "${clip(consulta, 120)}" en los ${total} mensajes de este chat y no encontré nada. Probá con otras palabras (el nombre del producto, de la campaña) o preguntale al usuario.`;
  }

  const tope = Math.max(1, Math.min(limite || RESULTADOS_POR_DEFECTO, MAX_RESULTADOS));
  const elegidos = [...aciertos]
    .sort((a, b) => b.puntaje - a.puntaje || b.indice - a.indice)
    .slice(0, tope)
    .sort((a, b) => a.indice - b.indice);

  const lineas = elegidos.map((h) => {
    const m = mensajes[h.indice];
    return `#${h.indice + 1}/${total} — ${QUIEN[m.role]}: ${recorte(textos[h.indice], h.posicion)}`;
  });

  return [
    `Encontré ${aciertos.length} mensaje(s) que hablan de "${clip(consulta, 120)}"${
      aciertos.length > elegidos.length ? `; te muestro los ${elegidos.length} más relevantes` : ''
    }, en orden cronológico (#1 es el primer mensaje del chat, #${total} el último):`,
    ...lineas,
    'Son recortes textuales del chat: usalos como lo que se dijo de verdad. Si necesitás más contexto de alguno, buscá con otras palabras de ese mismo tramo.',
  ].join('\n');
}
